import { useEffect, useMemo, useRef, useState } from 'react'
import { useEquipmentScope } from '../equipmentScope'
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react'
import { DragDropProvider, useDraggable, useDroppable } from '@dnd-kit/react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { useTeamSchedule, type WeekDay } from '@/hooks/useTeamSchedule'
import { eventMeta, PROCESS_EVENT_TYPE_CODE, type MaintenanceTask } from '@/types/maintenanceTask'
import { useEventTypeCatalog } from '@/hooks/useEventTypes'
import { formatDuration } from '@/lib/timeWindow'
import { SCHEDULE_STATUS_META, type ScheduleEvent, type Technician } from '@/types/teamSchedule'
import { ScheduleEventDialog } from './ScheduleEventDialog'
import { StartEventModal } from '@/components/equipment/mylist/StartEventModal'
import { EndEventModal } from '@/components/equipment/mylist/EndEventModal'
import { useTaskForm } from '@/hooks/useTaskForm'

const GRID = '200px repeat(5, minmax(180px, 1fr))'
const UNASSIGNED = 'unassigned'

const EVENT_LABEL: Record<string, string> = {
  preventive_maintenance: 'PM',
  corrective_maintenance: 'Corrective',
  corrective_action: 'Corrective action',
  cleaning: 'Cleaning',
  calibration: 'Calibration',
  inspection: 'Inspection',
  verification: 'Verification',
  performance_qualification: 'PQ',
  [PROCESS_EVENT_TYPE_CODE]: 'Process',
}

const LEGEND = [
  'preventive_maintenance',
  'corrective_maintenance',
  'corrective_action',
  'cleaning',
  'calibration',
  'inspection',
  PROCESS_EVENT_TYPE_CODE,
]

const TYPE_FILTERS = [
  { id: 'all', label: 'All types' },
  ...LEGEND.map((code) => ({ id: code, label: EVENT_LABEL[code] })),
  { id: 'verification', label: 'Verification' },
]

/** Drag payload from a card / drop payload for a cell. */
type CardData = { eventId: string; fromTechId: string; dayIso: string }

/**
 * A card id back to the MaintenanceEvent it belongs to.
 *
 * A repeating task draws one card per occurrence, keyed `<eventId>|<date>` so
 * React and the drag layer can tell two Mondays apart — but start, complete and
 * assign all act on the single task, so the suffix has to come off first.
 */
function taskIdOf(cardId: string): string {
  return cardId.split('|')[0]
}
type CellData = { techId: string; dayIso: string }

function DayHeader({ day }: { day: WeekDay }) {
  const weekday = day.date.toLocaleDateString(undefined, { weekday: 'short' }).toUpperCase()
  const md = `${String(day.date.getMonth() + 1).padStart(2, '0')}/${String(day.date.getDate()).padStart(2, '0')}`
  return (
    <div className={cn('flex items-center gap-2 border-l border-gray-100 px-3 py-2.5', day.isToday && 'bg-blue-50/60')}>
      <span className='text-[11px] font-bold uppercase tracking-wide text-gray-500'>{weekday}</span>
      <span className='text-[11px] font-medium text-gray-400'>{md}</span>
      {day.isToday && (
        <span className='ml-auto rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold text-primary-foreground'>
          Today
        </span>
      )}
    </div>
  )
}

function TechCell({ tech, count }: { tech: Technician; count: number }) {
  return (
    <div className='flex flex-col justify-center gap-2 px-3 py-3'>
      <div className='flex items-center gap-2.5'>
        <span className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-bold text-gray-600'>
          {tech.initial}
        </span>
        <div className='min-w-0'>
          <p className='truncate text-sm font-semibold text-gray-900'>{tech.name}</p>
          <p className='truncate text-[11px] text-gray-400'>{tech.role}</p>
        </div>
      </div>
      <span className='w-fit rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500'>
        {count} events
      </span>
    </div>
  )
}

function EventCard({ event }: { event: ScheduleEvent }) {
  // The tenant's name and colour for the type, never its code (z8nrz7fb9k).
  const catalog = useEventTypeCatalog()
  const meta = eventMeta(event.eventType, catalog)
  const status = SCHEDULE_STATUS_META[event.status]
  return (
    <div
      className='flex w-full items-center gap-2 rounded-md border-l-[3px] py-1.5 pl-2 pr-2 text-left shadow-sm transition-all hover:-translate-y-px hover:shadow-md'
      style={{ backgroundColor: `${meta.color}14`, borderColor: meta.color }}
    >
      <div className='min-w-0 flex-1'>
        <div className='flex items-center gap-1.5'>
          <span className='h-1.5 w-1.5 shrink-0 rounded-full' style={{ backgroundColor: meta.color }} />
          <span className='truncate text-[13px] font-semibold text-gray-800'>{event.equipmentId}</span>
        </div>
        <div className='mt-0.5 truncate text-[11px] font-medium' style={{ color: meta.color }}>
          {EVENT_LABEL[event.eventType] ?? meta.label} · {event.timeStart}
          {/* A booking states how long it holds the machine on THIS day —
              without it a 20-minute hold and an all-day one look identical. */}
          {event.readOnly && event.planHours > 0 && ` · ${formatDuration(event.planHours)}`}
        </div>
      </div>
      <div className='flex shrink-0 flex-col items-end gap-1'>
        <span className='h-2 w-2 rounded-full' style={{ backgroundColor: status.color }} />
        <span className='text-[10px] font-medium text-gray-400'>{status.label}</span>
      </div>
    </div>
  )
}

/** A card that can be dragged to another day / technician. Completed events are
 *  still clickable but the drop is ignored (see handleDragEnd). */
function DraggableCard({
  event,
  techId,
  dayIso,
  onClick,
}: {
  event: ScheduleEvent
  techId: string
  dayIso: string
  onClick: () => void
}) {
  const { ref, isDragging } = useDraggable({
    // The draggable is keyed by the CARD (unique per day for a repeat), but the
    // payload carries the TASK — every mutation has to reach the one row
    // underneath, not the synthetic per-occurrence id.
    id: event.id,
    data: { eventId: event.taskId ?? event.id, fromTechId: techId, dayIso } satisfies CardData,
  })
  return (
    <div
      ref={ref}
      onClick={onClick}
      className={cn('cursor-grab touch-none select-none active:cursor-grabbing', isDragging && 'opacity-30')}
    >
      <EventCard event={event} />
    </div>
  )
}

/** A droppable (technician × day) cell. */
function DroppableCell({
  techId,
  dayIso,
  isToday,
  children,
}: {
  techId: string
  dayIso: string
  isToday: boolean
  children: React.ReactNode
}) {
  const { ref, isDropTarget } = useDroppable({ id: `${techId}|${dayIso}`, data: { techId, dayIso } satisfies CellData })
  return (
    <div
      ref={ref}
      className={cn(
        'min-h-[92px] space-y-1.5 border-l border-gray-100 p-1.5 transition-colors',
        isToday && 'bg-blue-50/40',
        isDropTarget && 'bg-primary/10 outline-dashed outline-2 -outline-offset-2 outline-primary',
      )}
    >
      {children}
    </div>
  )
}

/** A task to land on: its week, with its dialog open (My Tasks' Open, z8nrz7fh6h). */
export interface ScheduleFocus {
  taskId: string
  /** The day the task is on — its due date — so the right week is shown. */
  day: string | null
}

export function TeamScheduleTab({
  focus = null,
  onFocused,
}: {
  focus?: ScheduleFocus | null
  /** Called once the task's dialog has opened, so the link is not re-applied. */
  onFocused?: () => void
} = {}) {
  // This page's share only (z8nrz7ewt2).
  const { purposes } = useEquipmentScope()
  const {
    technicians,
    hasUnassigned,
    assignableUsers,
    events,
    week,
    weekLabel,
    prevWeek,
    nextWeek,
    goToday,
    goToDate,
    weekStartIso,
    taskById,
    moveEvent,
    start,
    end,
  } = useTeamSchedule(purposes)
  const [typeFilter, setTypeFilter] = useState('all')
  const [techFilter, setTechFilter] = useState('all') // 'all' | techId | UNASSIGNED
  const [selected, setSelected] = useState<{ event: ScheduleEvent; dateLabel: string; technicianName: string } | null>(null)
  const [startTask, setStartTask] = useState<MaintenanceTask | null>(null)
  // What the unit still holds, when Start was refused for it: the dialog then
  // asks for the override reason up front.
  const [startHeld, setStartHeld] = useState<string | null>(null)
  const openStart = (task: MaintenanceTask | null, holdsStock: string | null = null) => {
    setStartTask(task)
    setStartHeld(holdsStock)
  }
  const [endTask, setEndTask] = useState<MaintenanceTask | null>(null)
  // A task with a form starts straight into it (z8nrz7fgzw), as on Tasks.
  const taskForm = useTaskForm()

  const visible = useMemo(
    () => (typeFilter === 'all' ? events : events.filter((e) => e.eventType === typeFilter)),
    [events, typeFilter],
  )

  const dayLabel = (day: WeekDay) =>
    day.date.toLocaleDateString(undefined, { weekday: 'short', month: '2-digit', day: '2-digit', year: 'numeric' })

  // A link naming a task: show its week, then open it once its card has
  // loaded. Applied once per task, so closing the dialog does not reopen it.
  const focused = useRef<string | null>(null)
  useEffect(() => {
    if (focus?.day) goToDate(focus.day)
    // Only when the link changes; goToDate is the hook's setter.
  }, [focus?.taskId, focus?.day]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!focus || focused.current === focus.taskId) return
    const event = events.find((e) => (e.taskId ?? e.id) === focus.taskId)
    if (!event) return
    focused.current = focus.taskId
    const day = week[event.dayOffset]
    const techName =
      event.technicianId === UNASSIGNED
        ? 'Unassigned'
        : (technicians.find((t) => t.id === event.technicianId)?.name ?? '')
    setSelected({ event, dateLabel: day ? dayLabel(day) : '', technicianName: techName })
    onFocused?.()
  }, [focus, events, week, technicians]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleDragEnd = (evt: { operation: { source?: { data: unknown } | null; target?: { data: unknown } | null }; canceled: boolean }) => {
    const { source, target } = evt.operation
    if (evt.canceled || !source || !target) return
    const card = source.data as CardData
    const cell = target.data as CellData
    // Completed events can't be rescheduled/reassigned (backend also enforces this).
    // Matched on the TASK, not the card: a repeating task's cards carry a
    // synthetic `<eventId>|<date>` id, so comparing against `e.id` would find
    // nothing and let a completed repeat be dragged.
    if (events.find((e) => (e.taskId ?? e.id) === card.eventId)?.status === 'done') return
    const newAssignee = cell.techId === UNASSIGNED ? '' : cell.techId
    const currentAssignee = card.fromTechId === UNASSIGNED ? '' : card.fromTechId
    const patch: { dueDate?: string; assigneeId?: string } = {}
    if (cell.dayIso !== card.dayIso) patch.dueDate = cell.dayIso
    if (newAssignee !== currentAssignee) patch.assigneeId = newAssignee
    if (patch.dueDate === undefined && patch.assigneeId === undefined) return
    // The card's own day travels with the drag so a repeating task can be
    // shifted by the DIFFERENCE, moving the whole series rather than dropping
    // its end date on the target day.
    moveEvent(card.eventId, patch, card.dayIso)
  }

  // Render one board row (a technician, or the Unassigned lane).
  const renderRow = (rowId: string, labelCell: React.ReactNode, techName: string, highlight = false) => (
    <div key={rowId} className={cn('grid border-b border-gray-100', highlight && 'bg-amber-50/20')} style={{ gridTemplateColumns: GRID }}>
      {labelCell}
      {week.map((day, i) => {
        const cellEvents = visible.filter((e) => e.technicianId === rowId && e.dayOffset === i)
        return (
          <DroppableCell key={day.iso} techId={rowId} dayIso={day.iso} isToday={day.isToday}>
            {cellEvents.map((event) =>
              // Bookings are shown, not managed: clickable for details, but no
              // drag handle at all rather than a draggable card whose drop is
              // silently ignored.
              event.readOnly ? (
                <button
                  key={event.id}
                  type='button'
                  className='block w-full text-left'
                  onClick={() =>
                    setSelected({ event, dateLabel: dayLabel(day), technicianName: techName })
                  }
                >
                  <EventCard event={event} />
                </button>
              ) : (
                <DraggableCard
                  key={event.id}
                  event={event}
                  techId={rowId}
                  dayIso={day.iso}
                  onClick={() => setSelected({ event, dateLabel: dayLabel(day), technicianName: techName })}
                />
              ),
            )}
          </DroppableCell>
        )
      })}
    </div>
  )

  return (
    <div className='overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm'>
      {/* Header */}
      <div className='border-b border-gray-100 p-4'>
        <div className='flex flex-wrap items-baseline gap-x-3 gap-y-1'>
          <h2 className='text-lg font-semibold tracking-tight text-gray-900'>Team Schedule</h2>
          <p className='text-xs text-muted-foreground'>
            Who is working on what — week of {weekLabel} · drag a task to reschedule or reassign
          </p>
        </div>

        <div className='mt-3 flex flex-wrap items-center gap-2.5'>
          <Select value={techFilter} onValueChange={setTechFilter}>
            <SelectTrigger className='h-8 w-[170px]'>
              <SelectValue placeholder='All technicians' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All technicians</SelectItem>
              {technicians.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.name}
                </SelectItem>
              ))}
              {hasUnassigned && <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>}
            </SelectContent>
          </Select>

          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className='h-8 w-[150px]'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TYPE_FILTERS.map((f) => (
                <SelectItem key={f.id} value={f.id}>
                  {f.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className='flex items-center gap-1 rounded-lg border border-gray-200 bg-gray-50 p-0.5'>
            <button type='button' onClick={prevWeek} aria-label='Previous week' className='inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-500 hover:bg-white hover:text-gray-900'>
              <ChevronLeft className='h-4 w-4' />
            </button>
            <button type='button' onClick={goToday} className='rounded-md px-3 py-1 text-xs font-semibold text-primary hover:bg-white'>
              Today
            </button>
            <button type='button' onClick={nextWeek} aria-label='Next week' className='inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-500 hover:bg-white hover:text-gray-900'>
              <ChevronRight className='h-4 w-4' />
            </button>
          </div>

          {/* Jump to any week by date. The board is a Mon–Fri grid, so a picked
              date resolves to the week containing it rather than a loose day —
              the value snaps back to that week's Monday to show what happened. */}
          <label className='flex items-center gap-1.5 rounded-lg border border-gray-200 px-2 py-1'>
            <CalendarDays className='h-4 w-4 shrink-0 text-gray-400' />
            <input
              type='date'
              aria-label='Jump to week containing date'
              value={weekStartIso}
              onChange={(e) => e.target.value && goToDate(e.target.value)}
              className='w-[130px] bg-transparent text-xs text-gray-700 outline-none'
            />
          </label>

          <div className='ml-auto hidden flex-wrap items-center gap-x-3 gap-y-1 xl:flex'>
            <span className='text-[11px] font-semibold uppercase tracking-wide text-gray-400'>Event types</span>
            {LEGEND.map((code) => (
              <span key={code} className='inline-flex items-center gap-1.5 text-xs text-gray-600'>
                <span className='h-2 w-2 rounded-full' style={{ backgroundColor: eventMeta(code).color }} />
                {EVENT_LABEL[code]}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Grid */}
      {taskForm.error && (
        <p className='text-xs font-medium text-red-600' role='alert'>
          {taskForm.error}
        </p>
      )}
      <DragDropProvider onDragEnd={handleDragEnd}>
        <div className='overflow-auto'>
          <div className='min-w-[1080px]'>
            {/* Column header */}
            <div className='grid border-b border-gray-200 bg-gray-50/70' style={{ gridTemplateColumns: GRID }}>
              <div className='px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400'>Technician</div>
              {week.map((day) => (
                <DayHeader key={day.iso} day={day} />
              ))}
            </div>

            {/* Assigned technician rows (respecting the technician filter) */}
            {technicians
              .filter((tech) => techFilter === 'all' || tech.id === techFilter)
              .map((tech) => {
                const count = visible.filter((e) => e.technicianId === tech.id).length
                return renderRow(tech.id, <TechCell tech={tech} count={count} />, tech.name)
              })}

            {technicians.length === 0 && !hasUnassigned && (
              <div className='px-3 py-12 text-center text-sm text-gray-400'>No scheduled events this week.</div>
            )}

            {/* Unassigned lane — surfaced separately so open work is easy to spot + assign */}
            {hasUnassigned && (techFilter === 'all' || techFilter === UNASSIGNED) && (
              <>
                <div className='border-y border-amber-100 bg-amber-50/50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-amber-700'>
                  Unassigned · drag onto a technician to assign
                </div>
                {renderRow(
                  UNASSIGNED,
                  <div className='flex items-center gap-2.5 px-3 py-3'>
                    <span className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-xs font-bold text-amber-700'>–</span>
                    <p className='text-sm font-semibold text-amber-800'>Unassigned</p>
                  </div>,
                  'Unassigned',
                  true,
                )}
              </>
            )}
          </div>
        </div>
      </DragDropProvider>

      <ScheduleEventDialog
        event={selected?.event ?? null}
        technicianName={selected?.technicianName ?? ''}
        dateLabel={selected?.dateLabel ?? ''}
        assignableUsers={assignableUsers}
        // The dialog hands back the CARD's id; every action below has to reach
        // the task underneath, which for a repeat is not the same string.
        onAssign={(id, assigneeId) => {
          moveEvent(taskIdOf(id), { assigneeId })
          setSelected(null)
        }}
        onClose={() => setSelected(null)}
        onStart={(id) => {
          const task = taskById.get(taskIdOf(id)) ?? null
          if (!task?.procedureTemplate) openStart(task)
          else void taskForm.start(task).then((r) => typeof r === 'object' && openStart(task, r.holdsStock))
          setSelected(null)
        }}
        onEnd={(id) => {
          const task = taskById.get(taskIdOf(id)) ?? null
          if (task?.procedureTemplate) void taskForm.open(task)
          else setEndTask(task)
          setSelected(null)
        }}
      />

      <StartEventModal
        task={startTask}
        holdsStock={startHeld}
        onClose={() => openStart(null)}
        onConfirm={async (id, payload) => {
          // A task with a form only reaches this dialog when its unit holds
          // stock: once started, it goes on to its form.
          const task = startTask
          await start(id, payload, task?.procedureTemplate ? () => void taskForm.open(task) : undefined)
          openStart(null)
        }}
      />
      <EndEventModal
        task={endTask}
        onClose={() => setEndTask(null)}
        onConfirm={async (id, payload) => {
          await end(id, payload)
          setEndTask(null)
        }}
      />
    </div>
  )
}
